"use client";

import { useState, useMemo, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import useAllLeagues from "@/hooks/useAllLeagues";

export default function LeagueFilterV3({
  selectedIds = [],
  onChange,
  className = "",
  anchorRef,
}) {
  const { leagues, loading } = useAllLeagues();
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [tempSelectedIds, setTempSelectedIds] = useState(selectedIds || []);
  const [viewMode, setViewMode] = useState("league");
  const triggerRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({});

  useEffect(() => {
    setTempSelectedIds(selectedIds || []);
  }, [selectedIds]);

  const updateDropdownPosition = () => {
    if (!triggerRef.current) return;
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const gap = 8;
    const padding = 8;
    let left;
    let width;
    if (anchorRef?.current) {
      const anchorRect = anchorRef.current.getBoundingClientRect();
      left = anchorRect.left + 10;
      width = Math.min(anchorRect.width * 0.95, 400);
    } else {
      left = triggerRect.left;
      width = Math.min(480, window.innerWidth - left - padding);
    }
    setDropdownStyle({
      position: "fixed",
      top: triggerRect.bottom + gap,
      left,
      width,
      zIndex: 99999,
    });
  };

  useLayoutEffect(() => {
    if (open && triggerRef.current) updateDropdownPosition();
  }, [open]);

  const normalizedLeagues = useMemo(
    () =>
      (leagues || []).map((l) => ({
        ...l,
        country: l.country || l.countryName || "Khác",
      })),
    [leagues],
  );

  const filteredLeagues = useMemo(() => {
    if (!keyword.trim()) return normalizedLeagues || [];
    const lower = keyword.toLowerCase();
    return (normalizedLeagues || []).filter((league) => {
      const nameVN = (league.nameVN || "").toLowerCase();
      const name = (league.name || "").toLowerCase();
      const country = (league.country || "").toLowerCase();
      return (
        nameVN.includes(lower) || name.includes(lower) || country.includes(lower)
      );
    });
  }, [normalizedLeagues, keyword]);

  const leaguesByCountry = useMemo(() => {
    const groups = {};
    for (const league of filteredLeagues) {
      const countryKey = league.country || "Khác";
      if (!groups[countryKey]) {
        groups[countryKey] = {
          country: countryKey,
          countryLogo: league.countryLogo || null,
          items: [],
        };
      }
      groups[countryKey].items.push(league);
    }
    return Object.values(groups);
  }, [filteredLeagues]);

  const handleToggleLeague = (id) => {
    setTempSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    const allIds = (filteredLeagues || []).map((l) => l.id);
    setTempSelectedIds(allIds);
  };

  const handleClearAll = () => {
    setTempSelectedIds([]);
  };

  const handleConfirm = () => {
    onChange?.(tempSelectedIds);
    setOpen(false);
  };

  const hasSelection = selectedIds && selectedIds.length > 0;
  const triggerLabel = hasSelection
    ? `${selectedIds.length} giải đấu`
    : "Tất cả giải đấu";

  return (
    <div className={`relative inline-block ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          setOpen((prev) => {
            if (prev) return false;
            updateDropdownPosition();
            return true;
          });
        }}
        className={`shrink-0 inline-flex items-center justify-between rounded-lg border text-[13px] font-medium whitespace-nowrap px-[10px] py-[7px] min-h-[38px] transition-colors min-w-[150px]
          ${hasSelection
            ? "bg-[var(--theme-primary)] text-[var(--theme-on-primary)] border-[var(--theme-primary)]"
            : "bg-[var(--theme-foreground)] text-[var(--theme-text)] border-[var(--theme-border)]"
          }
        `}
      >
        <span className="truncate">{triggerLabel}</span>
        <span className="ml-2 text-xs">▾</span>
      </button>

      {open &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            style={dropdownStyle}
            className="max-h-[60vh] flex flex-col rounded-xl bg-[var(--theme-foreground)] text-[var(--theme-text)] shadow-xl border border-[var(--theme-border)] md:w-[550px]"
          >
            <div className="flex items-center justify-between px-3 pt-2 pb-1.5 shrink-0">
              <div className="relative flex-1 min-w-0">
                <input
                  type="text"
                  placeholder="Tìm tên giải đấu"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  className="w-full rounded-lg border border-[var(--theme-border)] bg-[var(--theme-bg)] py-2 pl-2.5 pr-9 text-sm outline-none focus:border-[var(--theme-primary)]"
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[var(--theme-text-muted)]">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </span>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="ml-2 p-2 -m-2 rounded hover:bg-[var(--theme-bg)]"
              >
                ✕
              </button>
            </div>

            <div className="px-3 pb-2 border-b border-[var(--theme-border)] shrink-0">
              <div className="flex items-center justify-between text-xs uppercase">
                <span className="font-medium text-[var(--theme-text-muted)]">Xem theo</span>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => setViewMode("league")}
                    className={`px-3 py-1.5 rounded text-xs font-medium ${viewMode === "league"
                        ? "bg-[var(--theme-foreground)] text-[var(--theme-primary)] border border-[var(--theme-primary)]"
                        : "bg-[var(--theme-bg)] text-[var(--theme-text-muted)] border border-[var(--theme-border)]"
                      }`}
                  >
                    Giải đấu
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode("country")}
                    className={`px-3 py-1.5 rounded text-xs font-medium ${viewMode === "country"
                        ? "bg-[var(--theme-foreground)] text-[var(--theme-primary)] border border-[var(--theme-primary)]"
                        : "bg-[var(--theme-bg)] text-[var(--theme-text-muted)] border border-[var(--theme-border)]"
                      }`}
                  >
                    Quốc gia
                  </button>
                </div>
              </div>
            </div>

            <div className="flex-1 min-h-0 overflow-auto py-2 pl-1 pr-2">
              {loading ? (
                <div className="py-8 text-center text-sm text-[var(--theme-text-muted)]">
                  Đang tải danh sách giải đấu...
                </div>
              ) : filteredLeagues.length === 0 ? (
                <div className="py-8 text-center text-sm text-[var(--theme-text-muted)]">
                  Không tìm thấy giải đấu nào
                </div>
              ) : viewMode === "league" ? (
                <div className="grid grid-cols-2 gap-1.5">
                  {filteredLeagues.map((league) => {
                    const checked = tempSelectedIds.includes(league.id);
                    const displayName = league.nameVN || league.name || "";
                    return (
                      <button
                        key={league.id}
                        type="button"
                        onClick={() => handleToggleLeague(league.id)}
                        className="flex items-center gap-1.5 rounded border border-[var(--theme-border)] bg-[var(--theme-bg)] pl-0.5 pr-1.5 py-1.5 text-xs text-left transition-colors min-w-0 hover:bg-[var(--theme-accent)]/50"
                      >
                        <span
                          className={`flex shrink-0 items-center justify-center w-4 h-4 rounded border text-[9px] font-bold
                          ${checked
                              ? "bg-[#2563eb] border-[#2563eb] text-white"
                              : "border-[var(--theme-border)] bg-[var(--theme-foreground)]"
                            }
                        `}
                        >
                          {checked ? "✓" : ""}
                        </span>
                        {league.logo && (
                          <img
                            src={league.logo}
                            alt=""
                            className="w-5 h-5 object-contain shrink-0"
                          />
                        )}
                        <span className="min-w-0 truncate text-left" title={league.nameVN || league.name}>
                          {displayName}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  {leaguesByCountry.map((group) => (
                    <div key={group.country}>
                      <div className="flex items-center gap-1.5 px-1 py-0.5 text-[11px] font-semibold uppercase text-[var(--theme-text-muted)]">
                        {group.countryLogo && (
                          <img
                            src={group.countryLogo}
                            alt={group.country}
                            className="w-5 h-5 object-contain"
                          />
                        )}
                        <span>{group.country}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-1.5 mt-1">
                        {group.items.map((league) => {
                          const checked = tempSelectedIds.includes(league.id);
                          const displayName = league.nameVN || league.name || "";
                          return (
                            <button
                              key={league.id}
                              type="button"
                              onClick={() => handleToggleLeague(league.id)}
                              className="flex items-center gap-1.5 rounded border border-[var(--theme-border)] bg-[var(--theme-bg)] pl-0.5 pr-1.5 py-1.5 text-xs text-left transition-colors min-w-0 hover:bg-[var(--theme-accent)]/50"
                            >
                              <span
                                className={`flex shrink-0 items-center justify-center w-4 h-4 rounded border text-[9px] font-bold
                                ${checked
                                    ? "bg-[#2563eb] border-[#2563eb] text-white"
                                    : "border-[var(--theme-border)] bg-[var(--theme-foreground)]"
                                  }
                              `}
                              >
                                {checked ? "✓" : ""}
                              </span>
                              {league.logo && (
                                <img
                                  src={league.logo}
                                  alt=""
                                  className="w-5 h-5 object-contain shrink-0"
                                />
                              )}
                              <span className="min-w-0 truncate text-left" title={league.nameVN || league.name}>
                                {displayName}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-[var(--theme-border)] bg-[var(--theme-bg)] shrink-0">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="flex items-center gap-1.5 text-xs text-[var(--theme-text)]"
                >
                  <span
                    className={`flex shrink-0 items-center justify-center w-4 h-4 rounded border text-[10px] font-bold ${filteredLeagues.length > 0 && filteredLeagues.every((l) => tempSelectedIds.includes(l.id))
                        ? "bg-[#2563eb] border-[#2563eb] text-white"
                        : "border-[var(--theme-border)] bg-[var(--theme-foreground)]"
                      }`}
                  >
                    {filteredLeagues.length > 0 && filteredLeagues.every((l) => tempSelectedIds.includes(l.id)) ? "✓" : ""}
                  </span>
                  Chọn tất cả
                </button>
                <button
                  type="button"
                  onClick={handleClearAll}
                  className="flex items-center gap-1.5 text-xs text-[var(--theme-text)]"
                >
                  <span
                    className={`flex shrink-0 items-center justify-center w-4 h-4 rounded border text-[10px] font-bold ${tempSelectedIds.length === 0 ? "bg-[#2563eb] border-[#2563eb] text-white" : "border-[var(--theme-border)] bg-[var(--theme-foreground)]"
                      }`}
                  >
                    {tempSelectedIds.length === 0 ? "✓" : ""}
                  </span>
                  Bỏ chọn tất cả
                </button>
              </div>
              <button
                type="button"
                onClick={handleConfirm}
                className="shrink-0 rounded-lg bg-[var(--theme-primary)] px-4 py-1.5 text-xs font-semibold text-[var(--theme-on-primary)]"
              >
                Xác nhận
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

